import type { ReactNode } from 'react'

import { Alert, VStack } from '@enechain/ecloud-designsystem'

import { Modal, type ModalSize } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { UnsavedChangesDialog } from '~/components/common/UnsavedChangesDialog'
import { useUnsavedActionGuard } from '~/hooks/useUnsavedGuard'

type Props = {
  isOpen: boolean
  onClose: () => void
  title: string
  size?: ModalSize
  serverError: string | undefined
  handleSave: () => void
  isPending: boolean
  isSaveDisabled?: boolean
  saveLabel?: string
  cancelLabel?: string
  scrollBehavior?: 'inside' | 'outside'
  /**
   * 未保存の入力があるか (= `useCrudModalForm` の `isDirty` をそのまま渡す)。
   * `true` の間は閉じる操作 (× / キャンセル / Esc / オーバーレイ) を
   * 離脱確認ダイアログで受け止めてから `onClose` する。
   *
   * 省略時 (`undefined`) はガードしない。 デザイナー依頼のガードは代表モーダル
   * から段階適用しており、 未適用のモーダルは従来どおり即座に閉じる。
   */
  isDirty?: boolean
  children: ReactNode
}

export const CrudModalLayout = ({
  isOpen,
  onClose,
  title,
  size,
  serverError,
  handleSave,
  isPending,
  isSaveDisabled = false,
  saveLabel,
  cancelLabel,
  scrollBehavior,
  isDirty = false,
  children,
}: Props): React.ReactElement => {
  // 閉じる経路は DS `ModalView` が × / Esc / オーバーレイをすべて `onClose` に
  // 集約するため、 `Modal.onClose` と footer のキャンセルの 2 箇所を
  // `guardAction` 経由にすれば全経路がガードされる。
  const guard = useUnsavedActionGuard({
    isDirty,
    beforeUnload: false,
  })

  const footer = (
    <SaveCancelFooter
      cancelLabel={cancelLabel}
      isPending={isPending}
      isSaveDisabled={isSaveDisabled}
      onCancel={() => guard.guardAction(onClose)}
      onSave={handleSave}
      saveLabel={saveLabel}
    />
  )

  return (
    <>
      <Modal
        footer={footer}
        isOpen={isOpen}
        onClose={() => guard.guardAction(onClose)}
        scrollBehavior={scrollBehavior}
        size={size}
        title={title}
      >
        <VStack align="stretch" gap={4}>
          {serverError !== undefined && (
            <Alert whiteSpace="pre-line" status="error" title={serverError} />
          )}
          {children}
        </VStack>
      </Modal>
      <UnsavedChangesDialog
        isOpen={guard.isBlocked}
        onCancel={guard.cancel}
        onConfirm={guard.proceed}
      />
    </>
  )
}
